import Link from 'next/link';
import type React from 'react';
import { getLinkPropsForUrl } from '../../features/shared/components/link/utils/getLinkPropsForUrl';
import { useTranslation } from '../../features/shared/i18n/translate/useTranslation';
import { cn } from '../../features/shared/utils/cn';
import { VisuallyHidden } from '../components/VisuallyHidden';

const NewTag = ({ children }: { children: string }) => (
	<span
		className={cn(
			'absolute top-0',
			'w-[120px] h-6 mobile:h-8',
			'right-[-18%] top-[10%] mobile:right-[-7%] mobile-xl:-[-8%] tablet:right-[-12%]',
			'flex justify-center items-center',
			'bg-alert-light',
			'text-black font-semibold text-center text-[10px] mobile:text-[12px]',
			'origin-center',
		)}
		style={{
			transform: 'rotate(45deg)',
		}}
	>
		{children}
	</span>
);

interface TileProps {
	href: string;
	title: string;
	color: string;
	icon: React.ReactNode;
	isNew?: boolean;
}

const TileContent = ({
	color,
	icon,
	title,
	isNew,
	newLabel,
}: {
	color: string;
	icon: React.ReactNode;
	title: string;
	isNew?: boolean;
	newLabel: string;
}) => (
	<div
		className={cn(
			'relative overflow-hidden',
			'flex flex-row gap-4 items-center justify-start rounded-[12px] min-h-[70px] tablet:min-h-[98px]',
			'bg-surface',
			'px-3 py-4 tablet:px-6 tablet:py-8 h-[68px]',
			'transition-colors duration-200',
			'after:absolute after:inset-0 after:rounded-[12px] after:pointer-events-none after:z-10 group-focus-visible:after:ring-2 group-focus-visible:after:ring-inset group-focus-visible:after:ring-highlight',
		)}
		style={
			{
				'--tile-color': color,
				color,
			} as React.CSSProperties
		}
	>
		<div
			aria-hidden="true"
			className={cn(
				'absolute inset-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-200',
			)}
			style={{ backgroundColor: color }}
		/>
		<div
			aria-hidden="true"
			className={cn(
				'relative size-6 tablet:size-10 flex-shrink-0 rounded-[50%] flex items-center justify-center',
				'[&>svg]:m-[7px] [&>svg]:tablet:m-[5px]',
				'transition-colors duration-200',
				'bg-[var(--tile-color)] text-white group-hover:bg-white group-hover:text-[var(--tile-color)] group-focus-visible:bg-white group-focus-visible:text-[var(--tile-color)]',
			)}
		>
			{icon}
		</div>
		<span
			className={cn(
				'relative font-bold text-[15px] leading-[18px] mobile:leading-[25px] mobile:text-[18px] tablet:text-[21px]',
				'transition-colors duration-200',
				'group-hover:text-white group-focus-visible:text-white',
			)}
		>
			{title}
		</span>
		{isNew && <NewTag>{newLabel}</NewTag>}
	</div>
);

export const Tile = ({ href, title, color, icon, isNew }: TileProps) => {
	const { t } = useTranslation();
	const anchorStyle = 'group outline-none';
	const hrefProps = getLinkPropsForUrl(href);
	const isExternalLink = 'target' in hrefProps;
	const isHashLink = href.startsWith('#');
	const newLabel = t('account_overview_new_shortcut_label');

	const tileContent = (
		<TileContent
			color={color}
			icon={icon}
			title={title}
			isNew={isNew}
			newLabel={newLabel}
		/>
	);

	const externalLinkLabel = isExternalLink && (
		<VisuallyHidden as="span">{t('new_window_aria_label')}</VisuallyHidden>
	);

	// For hash-only links, use a plain anchor to ensure hashchange events work properly
	if (isHashLink) {
		return (
			<a href={href} className={anchorStyle}>
				{tileContent}
				{externalLinkLabel}
			</a>
		);
	}

	return (
		<Link {...hrefProps} legacyBehavior>
			<a {...hrefProps} className={anchorStyle}>
				{tileContent}
				{externalLinkLabel}
			</a>
		</Link>
	);
};
